import type { ReactNode } from "react"
import { useState } from "react"
import {
	IconCircleArrowsOppositeDirectionOutline18 as CircleArrows,
	IconFlag3Outline18 as Flag,
	IconFlagCheckeredOutline18 as FlagCheckered,
} from "nucleo-ui-outline-18"

import {
	closeLoopBodyToHtml,
	defaultCloseLoopProjectTemplate,
	defaultCloseLoopTemplate,
	defaultCloseLoopUpvoteTemplate,
	renderCloseLoopTemplate,
} from "@productlane/lib/src/close-loop-template"
import {
	Badge,
	Button,
	DropdownMenu,
	DropdownMenuCheckboxItem,
	SelectTrigger,
	Switch,
	SystemEvent,
} from "@productlane/ui"

import { isDevEnvironment } from "@/env"
import { useZeroWorkspace } from "@/lib/zero"
import { NotFound } from "@/screens/not-found"
import { SettingsHeader } from "@/screens/settings/settings-header"
import { CloseLoopTemplateEditor } from "@/screens/settings/settings-pages/close-loop-template-editor"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { DesignIssueHeader } from "./design-issue-header"

// Fictional example only: this page ships in the prod bundle.
const EXAMPLE = {
	contactName: "Mara Lindqvist",
	email: "mara@example.com",
	company: "Northwind",
	publicItemName: "CSV export for invoices",
	identifier: "ENG-412",
}

// Linear customer statuses as a workspace might name them.
const LINEAR_CUSTOMER_STATUSES = [
	"Active",
	"Customer",
	"Paying",
	"Closed Won",
	"Onboarding",
	"Trial",
	"Prospect",
	"Evaluating",
	"Churned",
	"Lost",
	"Closed Lost",
]

const body = (...paragraphs: string[]) =>
	["Hey {{ContactFirstName}},", ...paragraphs, "Cheers,\n{{AgentName}}"].join(
		"\n\n",
	)

type GroupKey = "paying" | "evaluating" | "former" | "none"

interface StatusGroup {
	key: GroupKey
	label: string
	timelineName: string
	statuses: string[]
	note: string
	versions: { label: string; text: string }[]
}

const GROUPS: StatusGroup[] = [
	{
		key: "paying",
		label: "Paying customers",
		timelineName: "Paying",
		statuses: ["Active", "Customer", "Paying", "Closed Won", "Onboarding"],
		note: "Today's message, unchanged.",
		versions: [{ label: "Today's message", text: defaultCloseLoopTemplate }],
	},
	{
		key: "evaluating",
		label: "Trial and prospect",
		timelineName: "Trial and prospect",
		statuses: ["Trial", "Prospect", "Evaluating"],
		note: "Adds the line that ties the ship to their request. No pricing, no call ask.",
		versions: [
			{
				label: "Version 1",
				text: body(
					"You asked for {{ShippedItem}}, and we just shipped it.",
					"Give it a try and tell us how it goes. A human on the team reads every reply.",
				),
			},
			{
				label: "Version 2",
				text: body(
					"Good news: we just shipped {{ShippedItem}}, the one you asked for.",
					"Give it a try and tell us how it goes. A human on the team reads every reply.",
				),
			},
			{
				label: "Version 3",
				text: body(
					"We just shipped {{ShippedItem}}. Thanks for asking for it.",
					"Tell us how it works for your team. A human on the team reads every reply.",
				),
			},
		],
	},
	{
		key: "former",
		label: "Former customers",
		timelineName: "Former customer",
		statuses: ["Churned", "Lost", "Closed Lost"],
		note: "Says the thing exists now and leaves the door open. Asks nothing of them.",
		versions: [
			{
				label: "Version 1",
				text: body(
					"You asked for {{ShippedItem}}, and we just shipped it.",
					"If you'd like to take a look, we're happy to help you get started.",
				),
			},
			{
				label: "Version 2",
				text: body(
					"We just shipped {{ShippedItem}}, which you asked for.",
					"Take a look whenever it suits you. Reply here with any question.",
				),
			},
			{
				label: "Version 3",
				text: body(
					"Good news: {{ShippedItem}} is live. You asked for it, so we wanted you to know.",
					"If it's useful to you, we can show you how it works.",
				),
			},
		],
	},
	{
		key: "none",
		label: "No status",
		timelineName: "No status",
		statuses: [],
		note: "Today's message. Also catches any status not mapped above.",
		versions: [{ label: "Today's message", text: defaultCloseLoopTemplate }],
	},
]

function NewBadge({ children = "New" }: { children?: string }) {
	return (
		<Badge size="sm" variant="green" className="flex-none">
			{children}
		</Badge>
	)
}

function CustomerStatusPicker({
	chosen,
	onToggle,
}: {
	chosen: string[]
	onToggle: (status: string, checked: boolean) => void
}) {
	const label =
		chosen.length === 0
			? "Unmapped statuses"
			: chosen.length === 1
				? chosen[0]
				: `${chosen.length} statuses`
	return (
		<DropdownMenu
			trigger={
				<SelectTrigger className="w-44 dark:bg-gray-700/70">
					{label}
				</SelectTrigger>
			}
			contentProps={{ align: "end" }}
			className="max-h-72 overflow-y-auto"
		>
			{LINEAR_CUSTOMER_STATUSES.map((status) => (
				<DropdownMenuCheckboxItem
					key={status}
					checked={chosen.includes(status)}
					onSelect={(e) => e.preventDefault()}
					onCheckedChange={(checked) => onToggle(status, checked === true)}
				>
					{status}
				</DropdownMenuCheckboxItem>
			))}
		</DropdownMenu>
	)
}

function GroupEditor({
	group,
	text,
	statuses,
	onText,
	onStatuses,
}: {
	group: StatusGroup
	text: string
	statuses: string[]
	onText: (text: string) => void
	onStatuses: (statuses: string[]) => void
}) {
	const [editorKey, setEditorKey] = useState(0)
	return (
		<div className="flex flex-col gap-2">
			<div className="flex items-start justify-between gap-4">
				<div className="flex min-w-0 flex-col gap-1">
					<div className="flex items-center gap-2 text-sm font-medium text-primary">
						{group.label}
						{group.key !== "paying" && group.key !== "none" && <NewBadge />}
					</div>
					<div className="flex flex-wrap gap-1">
						{statuses.length === 0 ? (
							<span className="text-xs text-tertiary">
								Companies with no Linear status, or one not mapped above
							</span>
						) : (
							statuses.map((s) => (
								<Badge key={s} size="sm" variant="light-gray">
									{s}
								</Badge>
							))
						)}
					</div>
				</div>
				{group.key !== "none" && (
					<CustomerStatusPicker
						chosen={statuses}
						onToggle={(status, checked) =>
							onStatuses(
								checked
									? [...statuses, status]
									: statuses.filter((s) => s !== status),
							)
						}
					/>
				)}
			</div>
			<CloseLoopTemplateEditor
				key={editorKey}
				label=""
				kind="reported"
				value={text}
				onChange={onText}
				onBlur={onText}
			/>
			{group.versions.length > 1 && (
				<div className="flex flex-wrap items-center gap-1.5 pt-1">
					<span className="mr-1 text-xxs text-tertiary">Draft copy:</span>
					{group.versions.map((version) => (
						<Button
							key={version.label}
							size="sm"
							variant={version.text === text ? "outline" : "ghost"}
							onClick={() => {
								onText(version.text)
								setEditorKey((k) => k + 1)
							}}
						>
							{version.label}
						</Button>
					))}
				</div>
			)}
		</div>
	)
}

function EmailPreview({
	group,
	text,
	agentName,
	workspaceName,
}: {
	group: StatusGroup
	text: string
	agentName: string
	workspaceName: string
}) {
	const rendered = renderCloseLoopTemplate({
		template: text,
		publicItemName: EXAMPLE.publicItemName,
		contactName: EXAMPLE.contactName,
		agentName,
	})
	return (
		<div className="flex w-[340px] flex-none flex-col gap-2">
			<div className="flex items-center gap-2 text-xs font-medium text-primary">
				{group.label}
			</div>
			<div className="rounded-[10px] bg-white p-5 text-[13px] leading-relaxed text-gray-800 shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:text-gray-200 dark:ring-white/10">
				<div className="mb-4 space-y-0.5 border-b border-gray-200 pb-3 text-xxs text-tertiary dark:border-gray-700/80">
					<div>From: {workspaceName}</div>
					<div>To: {EXAMPLE.email}</div>
				</div>
				<div
					className="[&_p:last-child]:!mb-0"
					dangerouslySetInnerHTML={{ __html: closeLoopBodyToHtml(rendered) }}
				/>
			</div>
		</div>
	)
}

const TIMELINE_LABELS = [
	{
		label: "Option 1: variant in front",
		line: (name: string) =>
			`${name} follow-up for ${EXAMPLE.identifier} sent by email to ${EXAMPLE.email}`,
	},
	{
		label: "Option 2: variant at the end",
		line: (name: string) =>
			`Follow-up for ${EXAMPLE.identifier} sent by email to ${EXAMPLE.email}, ${name.toLowerCase()} message`,
	},
	{
		label: "Option 3: status named",
		line: (name: string) =>
			`Follow-up for ${EXAMPLE.identifier} sent by email to ${EXAMPLE.email} (${name.toLowerCase()})`,
	},
]

function Section({
	crumb,
	note,
	children,
}: {
	crumb: string
	note?: string
	children: ReactNode
}) {
	return (
		<div className="space-y-3">
			<div className="text-xs font-medium text-tertiary">{crumb}</div>
			{note && (
				<div className="max-w-[704px] text-xxs text-tertiary">{note}</div>
			)}
			{children}
		</div>
	)
}

export function CloseLoopByStatusPage() {
	const workspace = useZeroWorkspace()
	const workspaceName = workspace?.name ?? "Your workspace"
	const agentName = `${workspaceName} Agent`

	const [texts, setTexts] = useState<Record<GroupKey, string>>(
		() =>
			Object.fromEntries(
				GROUPS.map((g) => [g.key, g.versions[0]!.text]),
			) as Record<GroupKey, string>,
	)
	const [statuses, setStatuses] = useState<Record<GroupKey, string[]>>(
		() =>
			Object.fromEntries(GROUPS.map((g) => [g.key, g.statuses])) as Record<
				GroupKey,
				string[]
			>,
	)

	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1440px] px-8 py-10 pb-24">
				<div className="mb-10 max-w-[704px] space-y-4">
					<DesignIssueHeader
						identifier="PRO-10841"
						title="Close-loop follow-up: shape the message by customer status"
						url="https://linear.app/productlane/issue/PRO-10841"
						summary="Every company gets the follow-up when its issue ships. This proposal picks the message by the company's Linear customer status: paying customers keep today's text, trial and prospect companies hear that what they asked for shipped, former customers get a short note with an open door. Only the issue message splits; project and upvote messages stay one each."
					/>
					<SettingsContainer>
						<div className="space-y-2 text-sm text-secondary">
							<div className="font-medium text-primary">Open decisions</div>
							<ol className="list-decimal space-y-1.5 pl-5">
								<li>
									Which draft copy per group. Use the version buttons under each
									editor.
								</li>
								<li>
									Mapping: a status picker per group (shown), or only our
									built-in name list with no picker.
								</li>
								<li>
									Do project and upvote messages split by status too? That makes
									twelve editors; this design keeps them at one each.
								</li>
								<li>
									Does &quot;No status&quot; keep its own editor, or always
									reuse the Paying message?
								</li>
								<li>Timeline label: which of the three options below.</li>
							</ol>
						</div>
					</SettingsContainer>
				</div>

				<div className="space-y-14">
					<Section crumb="Settings / AI / Close loop">
						<div className="max-w-[704px]">
							<SettingsHeader
								title="Close loop"
								subtitle="Follow-ups for the customers who asked, sent once their Linear issue or project ships."
							/>
							<SettingsContainer>
								<SettingItem
									Icon={CircleArrows}
									title="Send follow-ups automatically"
									description="The customer who asked hears back in their channel when the work ships."
									action={<Switch name="closeLoopAutoSend" checked />}
								/>
								<SettingItem
									Icon={Flag}
									className="pt-3"
									title="Shipped issue statuses"
									description="Linear issue statuses that count as shipped."
									action={
										<SelectTrigger className="w-40 dark:bg-gray-700/70">
											Done
										</SelectTrigger>
									}
								/>
								<SettingItem
									Icon={FlagCheckered}
									className="pt-3"
									title="Shipped project statuses"
									description="Linear project statuses that count as shipped."
									action={
										<SelectTrigger className="w-40 dark:bg-gray-700/70">
											Any completed
										</SelectTrigger>
									}
								/>
							</SettingsContainer>

							<SettingsContainer className="mt-4 flex flex-col gap-5 divide-none p-4">
								<div className="flex flex-col gap-1">
									<div className="flex items-center gap-2 text-sm font-medium text-primary">
										Message for completed issue
										<NewBadge>Changed</NewBadge>
									</div>
									<div className="text-xs text-tertiary">
										The company&apos;s Linear customer status picks the message.
									</div>
								</div>
								{GROUPS.map((group) => (
									<GroupEditor
										key={group.key}
										group={group}
										text={texts[group.key]}
										statuses={statuses[group.key]}
										onText={(text) =>
											setTexts((t) => ({ ...t, [group.key]: text }))
										}
										onStatuses={(next) =>
											setStatuses((s) => ({ ...s, [group.key]: next }))
										}
									/>
								))}
								<CloseLoopTemplateEditor
									label="Message for completed project"
									kind="reported"
									value={defaultCloseLoopProjectTemplate}
									onChange={() => undefined}
									onBlur={() => undefined}
								/>
								<CloseLoopTemplateEditor
									label="Message for an upvote"
									kind="upvoted"
									value={defaultCloseLoopUpvoteTemplate}
									onChange={() => undefined}
									onBlur={() => undefined}
								/>
							</SettingsContainer>
						</div>
					</Section>

					<Section
						crumb="Preview: what each group gets"
						note={`One example: ${EXAMPLE.contactName.split(" ")[0]} at ${EXAMPLE.company} asked for CSV export, and its issue just shipped. The item name comes from the public title, never the Linear issue title. The previews follow the editors above.`}
					>
						<div className="flex flex-wrap gap-6">
							{GROUPS.map((group) => (
								<EmailPreview
									key={group.key}
									group={group}
									text={texts[group.key]}
									agentName={agentName}
									workspaceName={workspaceName}
								/>
							))}
						</div>
					</Section>

					<Section
						crumb="Thread / Timeline"
						note="The sent line names the variant that went out. Three wordings, shown for the trial and former-customer cases."
					>
						<div className="max-w-[704px] space-y-6">
							{TIMELINE_LABELS.map((option) => (
								<div key={option.label} className="space-y-2">
									<div className="text-xxs text-tertiary">{option.label}</div>
									<div className="rounded-[10px] bg-white py-3 shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
										{[GROUPS[1]!, GROUPS[2]!].map((group, i) => (
											<SystemEvent
												key={group.key}
												icon={
													<CircleArrows className="h-3 min-h-3 w-3 min-w-3 flex-none text-secondary" />
												}
												timestamp={new Date("2026-10-08T14:12:00Z")}
												hasEventBelow={i === 0}
												hasEventAbove={i === 1}
											>
												<span className="truncate text-tertiary">
													{option.line(group.timelineName)}
												</span>
											</SystemEvent>
										))}
									</div>
								</div>
							))}
						</div>
					</Section>
				</div>
			</div>
		</div>
	)
}
